import { Document, Page, Text, View, StyleSheet, Font } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import ArialRegular from '../../fonts/Arial-Regular.ttf';
import ArialBold from '../../fonts/Arial-Bold.ttf';

Font.register({
    family: 'ArialPdf',
    fonts: [
        { src: ArialRegular, fontWeight: 400 },
        { src: ArialBold, fontWeight: 700 },
    ],
});

const EURO = '\u20ac';

const colors = {
    ink: '#111827',
    muted: '#6b7280',
    soft: '#f3f4f6',
    softer: '#f9fafb',
    line: '#e5e7eb',
    blue: '#1d4ed8',
    blueDark: '#1e3a8a',
    blueSoft: '#dbeafe',
    green: '#047857',
    greenSoft: '#d1fae5',
    white: '#ffffff',
};

const s = StyleSheet.create({
    page: {
        fontFamily: 'ArialPdf',
        fontSize: 8,
        color: colors.ink,
        backgroundColor: colors.softer,
        padding: 24,
    },
    shell: {
        backgroundColor: colors.white,
        borderRadius: 12,
        border: `1 solid ${colors.line}`,
        minHeight: '100%',
        overflow: 'hidden',
    },
    hero: {
        backgroundColor: colors.blueDark,
        paddingHorizontal: 22,
        paddingVertical: 18,
        color: colors.white,
    },
    heroTop: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
    },
    brand: {
        fontSize: 9,
        letterSpacing: 1.4,
        textTransform: 'uppercase',
        color: colors.blueSoft,
        marginBottom: 7,
    },
    title: {
        fontSize: 24,
        fontWeight: 700,
        color: colors.white,
    },
    subtitle: {
        fontSize: 8,
        color: colors.blueSoft,
        marginTop: 5,
    },
    orderBadge: {
        backgroundColor: colors.white,
        color: colors.blueDark,
        borderRadius: 8,
        paddingHorizontal: 12,
        paddingVertical: 9,
        minWidth: 132,
        alignItems: 'flex-end',
    },
    orderBadgeLabel: {
        fontSize: 7,
        color: colors.muted,
        textTransform: 'uppercase',
        letterSpacing: 0.7,
        marginBottom: 3,
    },
    orderBadgeValue: {
        fontSize: 14,
        fontWeight: 700,
        color: colors.blueDark,
    },
    content: {
        padding: 18,
    },
    infoGrid: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 14,
    },
    panel: {
        flexGrow: 1,
        flexBasis: 0,
        border: `1 solid ${colors.line}`,
        borderRadius: 8,
        padding: 11,
        backgroundColor: colors.white,
    },
    panelTint: {
        backgroundColor: '#f8fbff',
        borderColor: '#bfdbfe',
    },
    label: {
        fontSize: 7,
        fontWeight: 700,
        color: colors.blue,
        textTransform: 'uppercase',
        letterSpacing: 0.6,
        marginBottom: 5,
    },
    name: {
        fontSize: 12,
        fontWeight: 700,
        color: colors.ink,
        marginBottom: 5,
    },
    meta: {
        fontSize: 8,
        color: colors.muted,
        lineHeight: 1.4,
    },
    statRow: {
        flexDirection: 'row',
        gap: 8,
        marginBottom: 14,
    },
    stat: {
        flexGrow: 1,
        flexBasis: 0,
        backgroundColor: colors.soft,
        borderRadius: 7,
        paddingHorizontal: 10,
        paddingVertical: 8,
    },
    statLabel: {
        fontSize: 7,
        color: colors.muted,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 3,
    },
    statValue: {
        fontSize: 11,
        fontWeight: 700,
        color: colors.ink,
    },
    table: {
        border: `1 solid ${colors.line}`,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 14,
    },
    tableTitle: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 11,
        paddingVertical: 9,
        backgroundColor: colors.white,
        borderBottom: `1 solid ${colors.line}`,
    },
    tableTitleText: {
        fontSize: 11,
        fontWeight: 700,
        color: colors.ink,
    },
    countPill: {
        backgroundColor: colors.blueSoft,
        color: colors.blueDark,
        borderRadius: 99,
        paddingHorizontal: 8,
        paddingVertical: 3,
        fontSize: 7,
        fontWeight: 700,
    },
    head: {
        flexDirection: 'row',
        backgroundColor: colors.blue,
        paddingVertical: 7,
        paddingHorizontal: 8,
    },
    th: {
        color: colors.white,
        fontWeight: 700,
        fontSize: 6.6,
        textTransform: 'uppercase',
        letterSpacing: 0.25,
        textAlign: 'right',
    },
    row: {
        flexDirection: 'row',
        paddingVertical: 7,
        paddingHorizontal: 8,
        borderBottom: `1 solid ${colors.line}`,
        minHeight: 30,
    },
    rowAlt: {
        backgroundColor: '#fbfdff',
    },
    td: {
        fontSize: 7.5,
        color: colors.muted,
        textAlign: 'right',
    },
    productName: {
        fontSize: 8,
        fontWeight: 700,
        color: colors.ink,
        marginBottom: 2,
    },
    productCode: {
        fontSize: 6.5,
        color: colors.muted,
    },
    empty: {
        padding: 18,
        textAlign: 'center',
        color: colors.muted,
    },
    cProduct: { width: '27%', textAlign: 'left' },
    cQty: { width: '7%' },
    cUnit: { width: '10%' },
    cDiscountP: { width: '8%' },
    cDiscount: { width: '10%' },
    cNet: { width: '10%' },
    cTax: { width: '9%' },
    cVat: { width: '9%' },
    cTotal: { width: '10%' },
    bottom: {
        flexDirection: 'row',
        gap: 12,
        alignItems: 'flex-start',
    },
    noteBox: {
        flexGrow: 1,
        flexBasis: 0,
        border: `1 solid ${colors.line}`,
        borderRadius: 8,
        padding: 11,
        minHeight: 84,
    },
    noteGrid: {
        flexDirection: 'row',
        gap: 10,
    },
    noteColumn: {
        flexGrow: 1,
        flexBasis: 0,
    },
    detailLine: {
        marginBottom: 5,
    },
    detailLabel: {
        fontSize: 7,
        color: colors.muted,
        textTransform: 'uppercase',
        letterSpacing: 0.4,
        marginBottom: 2,
    },
    detailValue: {
        fontSize: 8,
        color: colors.ink,
        lineHeight: 1.35,
    },
    summaryBox: {
        width: 245,
        borderRadius: 10,
        overflow: 'hidden',
        border: `1 solid ${colors.line}`,
    },
    summaryHead: {
        backgroundColor: colors.ink,
        paddingHorizontal: 12,
        paddingVertical: 8,
    },
    summaryHeadText: {
        color: colors.white,
        fontSize: 10,
        fontWeight: 700,
    },
    summaryBody: {
        paddingHorizontal: 12,
        paddingVertical: 8,
        backgroundColor: colors.white,
    },
    summaryRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 4,
        borderBottom: `1 solid ${colors.line}`,
    },
    summaryLabel: {
        color: colors.muted,
        fontSize: 8,
    },
    summaryValue: {
        color: colors.ink,
        fontSize: 8,
        fontWeight: 700,
    },
    grandTotal: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: 8,
        backgroundColor: colors.greenSoft,
        borderRadius: 7,
        paddingHorizontal: 9,
        paddingVertical: 8,
    },
    grandLabel: {
        color: colors.green,
        fontSize: 9,
        fontWeight: 700,
        textTransform: 'uppercase',
    },
    grandValue: {
        color: colors.green,
        fontSize: 13,
        fontWeight: 700,
    },
    footer: {
        position: 'absolute',
        left: 42,
        right: 42,
        bottom: 30,
        flexDirection: 'row',
        justifyContent: 'space-between',
        color: colors.muted,
        fontSize: 7,
    },
});

const fmt = (val) => {
    const n = Number.parseFloat(val);
    return (Number.isFinite(n) ? n : 0).toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const money = (val) => `${fmt(val)} ${EURO}`;
const value = (val, fallback = '--') => val || fallback;

const Field = ({ label, children }) => (
    <View>
        <Text style={s.label}>{label}</Text>
        {children}
    </View>
);

const Stat = ({ label, value: statValue }) => (
    <View style={s.stat}>
        <Text style={s.statLabel}>{label}</Text>
        <Text style={s.statValue}>{statValue}</Text>
    </View>
);

const SummaryRow = ({ label, value: rowValue }) => (
    <View style={s.summaryRow}>
        <Text style={s.summaryLabel}>{label}</Text>
        <Text style={s.summaryValue}>{rowValue}</Text>
    </View>
);

const DetailLine = ({ label, children }) => (
    <View style={s.detailLine}>
        <Text style={s.detailLabel}>{label}</Text>
        <Text style={s.detailValue}>{value(children)}</Text>
    </View>
);

const OrderDetailPDF = ({ orderData = {}, items = [], summary = {} }) => {
    const { t } = useTranslation();
    const orderItems = Array.isArray(items) ? items : [];
    const deliveryDate = value(orderData.deliverydate);
    const quantity = summary.totalQuantity ?? orderData.totalqty;
    const itemsCount = summary.itemsCount ?? orderData.noOfItems ?? orderItems.length;
    const netAmount = summary.netAmount ?? orderData.subTotal;
    const discountAmount = summary.discountAmount ?? orderData.discountAmount;
    const taxes = summary.totalTaxes ?? orderData.total_tax_amount;
    const vat = summary.vats ?? orderData.vatAmount;
    const transportation = summary.transportationFee ?? orderData.totaltransportation;
    const finalAmount = summary.finalAmount ?? summary.totalAmount ?? orderData.finalAmount;

    return (
        <Document>
            <Page size="A4" style={s.page}>
                <View style={s.shell}>
                    <View style={s.hero}>
                        <View style={s.heroTop}>
                            <View>
                                <Text style={s.brand}>Intercool</Text>
                                <Text style={s.title}>{t('order detail')}</Text>
                                <Text style={s.subtitle}>{t('customer order summary')}</Text>
                            </View>
                            <View style={s.orderBadge}>
                                <Text style={s.orderBadgeLabel}>{t('order number')}</Text>
                                <Text style={s.orderBadgeValue}>#{value(orderData.orderid)}</Text>
                                <Text style={s.meta}>{value(orderData.orderdate)}</Text>
                            </View>
                        </View>
                    </View>

                    <View style={s.content}>
                        <View style={s.infoGrid}>
                            <View style={[s.panel, s.panelTint]}>
                                <Field label={t('bill to')}>
                                    <Text style={s.name}>{value(orderData.customerName)}</Text>
                                    <Text style={s.meta}>{t('customer code')}: {value(orderData.customerCode)}</Text>
                                    <Text style={s.meta}>{t('email')}: {value(orderData.customerEmail)}</Text>
                                    <Text style={s.meta}>{t('mobile')}: {value(orderData.customerMobile)}</Text>
                                </Field>
                            </View>
                            <View style={s.panel}>
                                <Field label={t('delivery')}>
                                    <Text style={s.name}>{value(orderData.deliveryaddressname, t('delivery address not provided'))}</Text>
                                    <Text style={s.meta}>{t('delivery date')}: {deliveryDate}</Text>
                                    <Text style={s.meta}>{t('transportation')}: {value(orderData.transportationcompanyname)}</Text>
                                </Field>
                            </View>
                        </View>

                        <View style={s.statRow}>
                            <Stat label={t('items')} value={value(itemsCount, orderItems.length)} />
                            <Stat label={t('total quantity')} value={fmt(quantity)} />
                            <Stat label={t('net amount')} value={money(netAmount)} />
                            <Stat label={t('discount')} value={money(discountAmount)} />
                            <Stat label={t('amount due')} value={money(finalAmount)} />
                        </View>

                        <View style={s.table}>
                            <View style={s.tableTitle}>
                                <Text style={s.tableTitleText}>{t('items ordered')}</Text>
                                <Text style={s.countPill}>{orderItems.length} {t('line items')}</Text>
                            </View>
                            <View style={s.head}>
                                <Text style={[s.th, s.cProduct, { textAlign: 'left' }]}>{t('product')}</Text>
                                <Text style={[s.th, s.cQty]}>{t('qty')}</Text>
                                <Text style={[s.th, s.cUnit]}>{t('unit price')}</Text>
                                <Text style={[s.th, s.cDiscountP]}>{t('disc')} %</Text>
                                <Text style={[s.th, s.cDiscount]}>{t('disc')}</Text>
                                <Text style={[s.th, s.cNet]}>{t('net')}</Text>
                                <Text style={[s.th, s.cTax]}>{t('extra taxes')}</Text>
                                <Text style={[s.th, s.cVat]}>{t('vat')}</Text>
                                <Text style={[s.th, s.cTotal]}>{t('total')}</Text>
                            </View>

                            {orderItems.length === 0 ? (
                                <Text style={s.empty}>{t('no items available')}</Text>
                            ) : (
                                orderItems.map((item, i) => (
                                    <View key={`${item.product_code || 'item'}-${i}`} style={[s.row, i % 2 === 1 && s.rowAlt]} wrap={false}>
                                        <View style={s.cProduct}>
                                            <Text style={s.productName}>{value(item.productname)}</Text>
                                            <Text style={s.productCode}>{value(item.product_code, '')}</Text>
                                        </View>
                                        <Text style={[s.td, s.cQty]}>{fmt(item.qty)}</Text>
                                        <Text style={[s.td, s.cUnit]}>{money(item.initprice)}</Text>
                                        <Text style={[s.td, s.cDiscountP]}>{item.percent_discount || 0}%</Text>
                                        <Text style={[s.td, s.cDiscount]}>{money(item.discountamount)}</Text>
                                        <Text style={[s.td, s.cNet]}>{money(item.subtotal)}</Text>
                                        <Text style={[s.td, s.cTax]}>{money(item.extra_taxes || 0)}</Text>
                                        <Text style={[s.td, s.cVat]}>{money(item.vatamount)}</Text>
                                        <Text style={[s.td, s.cTotal, { fontWeight: 700, color: colors.ink }]}>{money(item.finalamount)}</Text>
                                    </View>
                                ))
                            )}
                        </View>

                        <View style={s.bottom} wrap={false}>
                            <View style={s.noteBox}>
                                <Text style={s.label}>{t('order information')}</Text>
                                <View style={s.noteGrid}>
                                    <View style={s.noteColumn}>
                                        <DetailLine label={t('customer address')}>{orderData.customerAddress}</DetailLine>
                                        <DetailLine label={t('payment method')}>{orderData.paymentType}</DetailLine>
                                    </View>
                                    <View style={s.noteColumn}>
                                        <DetailLine label={t('delivery date')}>{orderData.deliverydate}</DetailLine>
                                        <DetailLine label={t('comments')}>{orderData.comment || t('no comments were added to this order')}</DetailLine>
                                    </View>
                                </View>
                            </View>

                            <View style={s.summaryBox}>
                                <View style={s.summaryHead}>
                                    <Text style={s.summaryHeadText}>{t('final summary')}</Text>
                                </View>
                                <View style={s.summaryBody}>
                                    <SummaryRow label={t('items')} value={value(itemsCount, orderItems.length)} />
                                    <SummaryRow label={t('total quantity')} value={fmt(quantity)} />
                                    <SummaryRow label={t('net amount')} value={money(netAmount)} />
                                    <SummaryRow label={t('discount')} value={money(discountAmount)} />
                                    <SummaryRow label={t('extra taxes')} value={money(taxes)} />
                                    <SummaryRow label={t('transportation')} value={money(transportation)} />
                                    <SummaryRow label={t('vat')} value={money(vat)} />
                                    <View style={s.grandTotal}>
                                        <Text style={s.grandLabel}>{t('total')}</Text>
                                        <Text style={s.grandValue}>{money(finalAmount)}</Text>
                                    </View>
                                </View>
                            </View>
                        </View>
                    </View>
                </View>

                <View style={s.footer} fixed>
                    <Text>Order #{value(orderData.orderid)} - {value(orderData.customerName)}</Text>
                    <Text render={({ pageNumber, totalPages }) => `${t('page')} ${pageNumber} ${t('of')} ${totalPages}`} />
                </View>
            </Page>
        </Document>
    );
};

export default OrderDetailPDF;
